import { useEffect, useRef, useState } from 'react'
import { AlertCircle, ArrowUp, CornerUpLeft, Hourglass, Info, Workflow } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { cn } from '@/lib/utils'
import { IntuiTextarea } from '@/components/intueri/IntuiTextarea'
import type { Period, RunState } from '../v4/contracts'
import AgentDot from './AgentDot'
import AskCard, { MethodFilePick } from './AskCard'
import PlanReadyCard from './PlanReadyCard'
import ResultCard from './ResultCard'
import RunActivity from './RunActivity'
import StepsCard from './StepsCard'
import type { LiveCardSpec, StepSeries } from './liveCards'
import { canSendFile, continuesRun, followUpConversation } from './runRules'
import { runStory } from './runStory'
import type { WorkflowRun } from './useWorkflowRun'

/** Statuses in which the engine itself is at work, so a bare "Thinking…" is true before its progress lands. */
const WORKING = new Set(['understanding', 'planning', 'running'])

/** An agent-status line in the old chat's words: the logo dot and a pulsing label. */
function Pulse({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
      <AgentDot />
      <span className="animate-pulse">{label}</span>
    </div>
  )
}

/** No procedure fits (F6): what was searched, what exists, and the two doors the ask holds. */
function Refusal({ refusal }: { refusal: NonNullable<RunState['refusal']> }) {
  const { t } = useTranslation()
  return (
    <div className="rounded-lg border border-border bg-muted p-3.5 text-sm dark:bg-dark-muted">
      <p className="flex items-center gap-2 font-medium text-foreground">
        <Info className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        {t('workflow:ask.refusal_title', { defaultValue: 'No procedure fits.' })}
      </p>
      {refusal.searched && refusal.searched.length > 0 && (
        <>
          <p className="mt-2 text-xs text-muted-foreground">{t('workflow:ask.refusal_searched', { defaultValue: 'Searched:' })}</p>
          <ul className="list-disc pl-5 text-foreground">
            {refusal.searched.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </>
      )}
      {refusal.existing && refusal.existing.length > 0 && (
        <>
          <p className="mt-2 text-xs text-muted-foreground">
            {t('workflow:ask.refusal_existing', { defaultValue: 'Available documents:' })}
          </p>
          <ul className="list-disc pl-5 text-foreground">
            {refusal.existing.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </>
      )}
      <p className="mt-2 text-xs text-muted-foreground">
        {t('workflow:ask.refusal_note', {
          defaultValue: 'Send a method file, or choose to talk it through in the question above.',
        })}
      </p>
    </div>
  )
}

/**
 * The run's conversation in the floating window, told the way the old chat told it: the question,
 * then the run's story (run/runStory.ts) as cards and lines in order: the plan to approve with its
 * Run box, each ask as a card the engineer answers, the steps as they run with what they found,
 * the engine's one-line remarks, and the answer with View report. Under it, what the engine is
 * doing right now (RunActivity), and the method-file door while a file can still shape the plan.
 * The composer starts a run from a question; with a run open it starts a new one (a follow-up on
 * the same run is a later layer).
 */
export default function RunChat({
  wf,
  onStarted,
  onViewReport,
  onSelectStep,
  onRerun,
  liveCards,
  onAddLiveCard,
}: {
  wf: WorkflowRun
  /** A run was started from the composer: the page follows it. */
  onStarted: (runId: string) => void
  /** The result card's View report: the page opens its Report tab. */
  onViewReport?: () => void
  /** A step row in the steps card: the page opens the step's inspector. */
  onSelectStep?: (stepId: string) => void
  /** The result card's rerun: the approved plan on another period, as a new run the page follows. */
  onRerun?: (period: Period) => Promise<unknown>
  /** The run's live cards (drawn on the canvas, not here) and the add action; the page owns them. */
  liveCards?: LiveCardSpec[]
  onAddLiveCard?: (series: StepSeries, title: string, stepId?: string) => void
}) {
  const { t } = useTranslation()
  const [question, setQuestion] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const run = wf.run
  const story = run ? runStory(run) : []
  const awaiting = run?.status === 'awaiting_approval'
  const fileOk = run ? canSendFile(run) : false
  const active = Boolean(run && run.status !== 'finished')

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [run?.events.length, run?.status])

  // Over an answered run the composer asks a follow-up: the engine builds on this run's plan.
  const followUp = Boolean(run?.trace.answer?.headline)

  const send = async () => {
    const text = question.trim()
    if (!text || wf.busy) return
    const conversation = run ? followUpConversation(run, text) : null
    const id = conversation ? await wf.start(text, conversation) : await wf.start(text)
    if (id) {
      setQuestion('')
      onStarted(id)
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scrollRef}
        data-testid="run-chat-transcript"
        className={cn('min-h-0 flex-1 overflow-y-auto px-4 pb-3', run ? 'space-y-4' : 'flex flex-col')}
      >
        {run ? (
          <>
            {run.earlier && (
              // A follow-up: the run it builds on, one click away.
              <button
                type="button"
                onClick={() => onStarted(run.earlier!.runId)}
                className="flex w-full items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted/50 dark:hover:bg-dark-muted/50"
              >
                <CornerUpLeft className="size-3.5 shrink-0" aria-hidden />
                <span className="min-w-0 truncate">
                  {t('workflow:builder.follows', { defaultValue: 'Follows: {{question}}', question: run.earlier.question })}
                </span>
              </button>
            )}
            <div className="ml-8 rounded-2xl rounded-br-md bg-muted px-3 py-2 text-sm text-foreground dark:bg-dark-muted">
              {run.question}
            </div>
            {story.map((item) => {
              switch (item.kind) {
                case 'ask': {
                  const ask = run.trace.asks.find((a) => a.askId === item.askId)
                  return ask ? (
                    <AskCard
                      key={`ask:${item.seq}`}
                      ask={ask}
                      open={ask.askId === run.openAskId}
                      busy={wf.busy}
                      onAnswer={wf.answer}
                      onFile={wf.sendFile}
                    />
                  ) : null
                }
                case 'plan':
                  return (
                    <PlanReadyCard
                      key={`plan:${item.plan.version}`}
                      plan={item.plan}
                      card={item.card}
                      current={item.plan.version === run.planVersion}
                      awaiting={awaiting && item.plan.version === run.planVersion}
                      continues={continuesRun(run)}
                      busy={wf.busy}
                      onRun={() => void wf.approve()}
                      fileEnabled={fileOk}
                      onFile={wf.sendFile}
                    />
                  )
                case 'steps':
                  return <StepsCard
                      key={`steps:${item.plan.version}`}
                      run={run}
                      plan={item.plan}
                      onSelectStep={onSelectStep}
                      liveCards={liveCards}
                      onAddLiveCard={onAddLiveCard}
                    />
                case 'result':
                  return (
                    <ResultCard
                      key={`result:${item.seq}`}
                      run={run}
                      text={item.text}
                      busy={wf.busy}
                      onViewReport={onViewReport}
                      onRerun={onRerun}
                    />
                  )
                default:
                  // The engine's remark, as the old chat drew assistant speech: plain text, no bubble.
                  return (
                    <p key={`line:${item.seq}`} className="text-sm leading-relaxed text-foreground">
                      {item.text}
                    </p>
                  )
              }
            })}
            {run.refusal && <Refusal refusal={run.refusal} />}
            {/* Before a plan exists a file can still shape it; once a plan awaits approval, its card holds the door. */}
            {fileOk && !awaiting && (
              <div className="rounded-lg border border-dashed border-border p-3.5 text-sm">
                <MethodFilePick
                  label={t('workflow:ask.send_file', { defaultValue: 'Send a method file' })}
                  enabled={!wf.busy}
                  onFile={wf.sendFile}
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  {t('workflow:ask.send_file_note', {
                    defaultValue: 'PDF, Markdown, or plain text, at most 7 MB. Send the file before approval.',
                  })}
                </p>
              </div>
            )}
            {active &&
              (wf.progress ? (
                <RunActivity progress={wf.progress} receivedAt={wf.progressAt} />
              ) : WORKING.has(run.status) ? (
                <Pulse label={t('workflow:builder.thinking', { defaultValue: 'Thinking…' })} />
              ) : null)}
          </>
        ) : wf.loading ? null : (
          // The old chat's seed (Figma "3. Create Workflow"): centred until the first message lands.
          <div className="flex flex-1 flex-col items-center justify-center gap-2 py-10 text-center">
            <span className="grid size-8 place-items-center rounded-lg bg-muted text-foreground dark:bg-dark-muted">
              <Workflow className="size-4" />
            </span>
            <p className="text-sm font-semibold text-foreground">
              {t('workflow:builder.chat_seed_run_title', { defaultValue: 'Build your workflow.' })}
            </p>
            <p className="max-w-[34ch] text-sm text-muted-foreground">
              {t('workflow:builder.chat_intro_run', {
                defaultValue:
                  'Ask a question about the plant. The engine works out a plan, shows it on the canvas and waits for your approval before it runs.',
              })}
            </p>
          </div>
        )}
        {wf.loading && <Pulse label={t('workflow:builder.loading_run', { defaultValue: 'Loading the run…' })} />}
        {wf.slow && !wf.error && (
          <div
            role="status"
            className="flex gap-2 rounded-lg border border-border bg-muted p-3 text-sm text-muted-foreground dark:bg-dark-muted"
          >
            <Hourglass className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              {wf.slow === 'running'
                ? t('workflow:builder.still_working', {
                    defaultValue:
                      'This is taking longer than usual. The workflow is still working on it, so there is nothing you need to do: the steps update here as it moves on.',
                  })
                : t('workflow:builder.not_started_yet', {
                    defaultValue:
                      'This is taking longer than usual, and the workflow has not shown up here yet. Check your workflows in a minute, or ask again.',
                  })}
            </span>
          </div>
        )}
        {wf.error && (
          <div
            role="alert"
            className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-100"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>{wf.error}</span>
          </div>
        )}
      </div>
      <div className="shrink-0 border-t border-border p-3">
        <div className="rounded-xl border border-border bg-background p-2 dark:bg-dark-card">
          <IntuiTextarea
            data-testid="run-composer"
            aria-label={t('workflow:builder.ask_label', { defaultValue: 'Your question' })}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault()
                void send()
              }
            }}
            minRows={2}
            maxRows={6}
            autoResize
            disabled={wf.busy}
            placeholder={
              followUp
                ? t('workflow:builder.ask_follow_up_placeholder', { defaultValue: 'Ask a follow-up…' })
                : t('workflow:builder.ask_placeholder', { defaultValue: 'Ask about the plant…' })
            }
            className="border-0 px-2 py-1.5 text-[13px] leading-snug shadow-none focus-visible:border-0 focus-visible:shadow-none"
          />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void send()}
              disabled={wf.busy || !question.trim()}
              aria-label={t('workflow:builder.ask_send', { defaultValue: 'Ask' })}
              className="grid size-8 place-items-center rounded-lg bg-brand text-white transition-opacity disabled:opacity-50"
            >
              <ArrowUp className="size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
